import React, { useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  LinearProgress, Link, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import VehicleService, { VehicleImportRow, VehicleImportSummary } from '../../api/services/vehicle.service';
import { parseCsvToObjects, toCsv, downloadCsv } from '../../utils/csv';

// Bringing a fleet spreadsheet in. Same steps as the candidate and team
// importers — template, choose, check, import — so nobody learns it twice.
//
// Matched on unit number: a truck already in the fleet is updated with the
// cells the file fills, never blanked by the ones it leaves empty, so the
// same file can go up again after a fix.

/**
 * Header spellings accepted. The template's own, plus the owner's vehicle
 * sheet's ("Unit No.", "Plate #", "Parked", "Lien", "Monthly Premium"…), so
 * that sheet imports without renaming. Matching ignores case, spaces,
 * underscores and dashes (parseCsvToObjects); punctuation stays, hence the
 * "unitno." spellings.
 */
export const FIELD_ALIASES: Record<string, string> = {
  unit: 'unit', unitno: 'unit', 'unitno.': 'unit', unitnumber: 'unit', 'unit#': 'unit', vannumber: 'unit',
  station: 'station', parked: 'station', serviceareaid: 'station', servicearea: 'station',
  vin: 'vin', vinno: 'vin', 'vinno.': 'vin', 'vin#': 'vin',
  year: 'year', make: 'make', model: 'model',
  bodytype: 'body_type', body: 'body_type', size: 'body_type',
  fuel: 'fuel', fueltype: 'fuel',
  plate: 'plate', 'plate#': 'plate', licenseplate: 'plate', plateno: 'plate',
  platestate: 'plate_state', state: 'plate_state',
  title: 'title', 'title#': 'title', titlenumber: 'title',
  registrationexpires: 'registration_expires', 'registrationexp.date': 'registration_expires',
  registrationexpdate: 'registration_expires', registrationexpiry: 'registration_expires',
  registrationreceived: 'registration_received',
  insurancerenews: 'insurance_renews', insurance: 'insurance_renews',
  scheduleb: 'schedule_b',
  propertytax: 'property_tax_amount', propertytaxamount: 'property_tax_amount',
  'propertytax#': 'property_tax_account', propertytaxaccount: 'property_tax_account', propertynumber: 'property_tax_account',
  ownership: 'ownership',
  lienholder: 'lienholder', lien: 'lienholder', lessor: 'lienholder',
  financingnotes: 'financing_notes', financing: 'financing_notes',
  startdate: 'start_date', effectivedate: 'start_date',
  maturitydate: 'maturity_date', matures: 'maturity_date',
  'term(months)': 'term', term: 'term',
  invoicevalue: 'invoice_value', purchaseprice: 'invoice_value',
  amountfinanced: 'amount_financed', equipmentcostfinanced: 'amount_financed',
  taxfinanced: 'tax_financed', taxcostfinanced: 'tax_financed',
  downpayment: 'down_payment',
  monthlypayment: 'monthly_payment', monthlypremium: 'monthly_payment',
  totalcost: 'total_cost',
  rate: 'rate', interestrate: 'rate',
  notes: 'notes',
};

const TEMPLATE_HEADERS = [
  'Unit No.', 'Station', 'VIN', 'Year', 'Make', 'Model', 'Body type', 'Fuel',
  'Plate', 'Plate state', 'Title #', 'Registration expires', 'Registration received',
  'Insurance renews', 'Schedule B', 'Property tax #', 'Property tax',
  'Ownership', 'Lienholder', 'Financing notes', 'Start date', 'Maturity date',
  'Term (months)', 'Invoice value', 'Amount financed', 'Tax financed',
  'Down payment', 'Monthly payment', 'Total cost', 'Rate', 'Notes',
];

// A financed truck filled in, and the other truth about this file: a unit
// number and a VIN is a perfectly good row — the VIN fills in the rest.
const TEMPLATE_EXAMPLE = [
  ['534128', '220', '1F65F5KN0N0A14937', '2022', 'Ford', 'F59', 'WALK IN P1000', 'Gas',
    'UB92710', 'VA', '2718963342', '12/31/2026', 'Yes',
    '7/1/2026', 'Yes', '26-1003-0938-5', '1429.57',
    'Financed', 'Verdant Commercial Capital, LLC', '72 payments, 0 down', '10/28/2024', '09/28/2030',
    '72', '61199.00', '61449.00', '2845.00',
    '0.00', '1165.42', '83909.88', '9.20%', ''],
  ['538231', '230', '1F65F5KN7S0A02022', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
];

const ImportVehiclesDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}> = ({ open, onClose, onImported }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [unknownHeaders, setUnknownHeaders] = useState<string[]>([]);
  const [results, setResults] = useState<VehicleImportRow[] | null>(null);
  const [summary, setSummary] = useState<VehicleImportSummary | null>(null);
  const [checked, setChecked] = useState(false);
  const [committed, setCommitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setFileName(''); setRows([]); setUnknownHeaders([]);
    setResults(null); setSummary(null);
    setChecked(false); setCommitted(false); setBusy(false); setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (busy) return;
    if (committed) onImported();
    reset();
    onClose();
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(''); setResults(null); setSummary(null);
    setChecked(false); setCommitted(false);
    try {
      const parsed = parseCsvToObjects(await file.text(), FIELD_ALIASES);
      // Rows with no unit and nothing else are the blank spacer lines a
      // sheet keeps between groups — not worth reporting as skipped.
      const data = parsed.rows.filter((row) => Object.values(row).some((v) => v));
      if (!data.length) {
        setError('That file has no data rows.');
        return;
      }
      setFileName(file.name);
      setRows(data);
      setUnknownHeaders(parsed.unknownHeaders.filter(Boolean));
    } catch (err: any) {
      setError(`Could not read that file: ${err.message}`);
    }
  };

  const send = async (dryRun: boolean) => {
    setBusy(true);
    setError('');
    try {
      const response = await VehicleService.import(rows, dryRun);
      setResults(response.results);
      setSummary(response.summary);
      if (dryRun) setChecked(true); else setCommitted(true);
    } catch (err: any) {
      const body = err?.response?.data;
      setError(body?.message || body?.error || 'The import failed. Nothing was written.');
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () =>
    downloadCsv('toposds-fleet-template.csv', toCsv(TEMPLATE_HEADERS, TEMPLATE_EXAMPLE));

  const ready = summary ? summary.created + summary.updated : 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Import vehicles</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!rows.length && (
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              Fill in the template and upload it. Only the unit number is required — with a VIN,
              year, make and model fill themselves in.
            </Typography>
            <Link component="button" type="button" onClick={downloadTemplate} sx={{ mb: 3, display: 'inline-block' }}>
              Download the template
            </Link>
            <Box>
              <Button variant="contained" disableElevation component="label" startIcon={<UploadIcon />}>
                Choose CSV file
                <input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden onChange={handleFile} />
              </Button>
            </Box>
          </Box>
        )}

        {rows.length > 0 && (
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" gutterBottom>
              <strong>{fileName}</strong> · {rows.length} row{rows.length === 1 ? '' : 's'}
            </Typography>

            {unknownHeaders.length > 0 && (
              <Alert severity="warning" sx={{ my: 2 }}>
                Ignored columns: {unknownHeaders.join(', ')}
              </Alert>
            )}

            {busy && <LinearProgress sx={{ my: 2 }} />}

            {summary && results && (
              <>
                <Stack direction="row" gap={1} sx={{ my: 2 }} flexWrap="wrap">
                  {summary.created > 0 && (
                    <Chip color="success" size="small" label={`${summary.created} new`} />
                  )}
                  {summary.updated > 0 && (
                    <Chip color="primary" size="small" variant="outlined" label={`${summary.updated} updated`} />
                  )}
                  {summary.skipped > 0 && (
                    <Chip size="small" variant="outlined" label={`${summary.skipped} skipped`} />
                  )}
                  {summary.filledFromVin > 0 && (
                    <Chip size="small" variant="outlined" label={`${summary.filledFromVin} filled from VIN`} />
                  )}
                </Stack>

                {summary.vinLookupUnavailable && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    The VIN lookup did not answer, so nothing was filled from VINs. Everything in the file still imports.
                  </Alert>
                )}

                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 340 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 64 }}>Row</TableCell>
                        <TableCell sx={{ width: 120 }}>Unit</TableCell>
                        <TableCell>Result</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.map((r) => (
                        <TableRow key={r.row}>
                          {/* +2: the header line, and spreadsheets count from 1. */}
                          <TableCell>{r.row + 2}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{r.unit || <em>blank</em>}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color={r.ok ? 'text.primary' : 'text.secondary'}>
                              {r.ok ? (r.action === 'create' ? 'New' : 'Updated') : r.message}
                            </Typography>
                            {!!r.warnings?.length && (
                              <Typography variant="caption" sx={{ color: 'warning.dark', display: 'block' }}>
                                {r.warnings.join(' · ')}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {committed && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Done. {summary?.written ?? 0} vehicle{summary?.written === 1 ? '' : 's'} saved.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={busy}>{committed ? 'Close' : 'Cancel'}</Button>
        {rows.length > 0 && !committed && !checked && (
          <Button variant="outlined" onClick={() => send(true)} disabled={busy}>Check the file</Button>
        )}
        {checked && !committed && (
          <Button variant="contained" disableElevation onClick={() => send(false)} disabled={busy || ready === 0}>
            Import {ready} vehicle{ready === 1 ? '' : 's'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportVehiclesDialog;
